/** Genre-aware Operate resource bindings for editor widgets (mirrors backend). */

const DEFAULT_WIDGET_BINDINGS = {
  blog: { resource: 'posts', title: 'title', body: 'excerpt', image: 'image_url', meta: '', meta_prefix: '', layout: 'card' },
  portfolio: { resource: 'projects', title: 'title', body: 'description', image: 'image_url', meta: '', meta_prefix: '', layout: 'card' },
  pricing: { resource: 'plans', title: 'name', body: 'description', image: '', meta: 'price', meta_prefix: '$', layout: 'card' },
  'features-grid': { resource: 'plans', title: 'name', body: 'highlights', image: '', meta: 'price', meta_prefix: '$', layout: 'feature' },
  'icon-section': { resource: 'plans', title: 'name', body: 'highlights', image: '', meta: 'price', meta_prefix: '$', layout: 'feature' },
  team: { resource: 'team', title: 'name', body: 'role', image: '', meta: '', meta_prefix: '', layout: 'card' },
  reviews: { resource: 'reviews', title: 'customer_name', body: 'comment', image: '', meta: 'rating', meta_prefix: '★', layout: 'card' },
  quiz: { resource: 'leads', configResource: 'widget_configs', title: 'name', body: 'message', image: '', meta: 'status', meta_prefix: '', layout: 'card' },
  'comment-area': { resource: 'comments', title: 'name', body: 'message', image: '', meta: 'status', meta_prefix: '', layout: 'list' },
  newsletter: { resource: 'subscribers', title: 'email', body: 'name', image: '', meta: 'status', meta_prefix: '', layout: 'list' },
  form: { resource: 'inquiries', title: 'name', body: 'message', image: '', meta: 'status', meta_prefix: '', layout: 'card' },
  booking: { resource: 'bookings', title: 'guest_name', body: 'email', image: '', meta: 'status', meta_prefix: '', layout: 'card' },
  menu: { resource: 'menu_items', title: 'name', body: 'description', image: 'image_url', meta: 'price', meta_prefix: '$', layout: 'menu' },
  services: { resource: 'services', title: 'name', body: 'description', image: '', meta: 'price', meta_prefix: '$', layout: 'service' },
  courses: { resource: 'courses', title: 'title', body: 'description', image: '', meta: 'price', meta_prefix: '$', layout: 'card' },
  listings: { resource: 'properties', title: 'title', body: 'address', image: 'image_url', meta: 'price', meta_prefix: '$', layout: 'card' },
  classes: { resource: 'schedules', title: 'name', body: 'days', image: '', meta: 'time', meta_prefix: '', layout: 'card' },
  tickets: { resource: 'events', title: 'title', body: 'venue', image: 'image_url', meta: 'price', meta_prefix: '$', layout: 'card' },
  'product-categories': { resource: 'categories', title: 'name', body: 'description', image: '', meta: '', meta_prefix: '', layout: 'card' },
};

const GENRE_WIDGET_OVERRIDES = {
  business: {
    'features-grid': { resource: 'services', title: 'name', body: 'description', image: '', meta: 'price', meta_prefix: '$', layout: 'feature' },
    'icon-section': { resource: 'services', title: 'name', body: 'description', image: '', meta: 'price', meta_prefix: '$', layout: 'feature' },
  },
  agency: {
    portfolio: DEFAULT_WIDGET_BINDINGS.portfolio,
    'features-grid': { resource: 'services', title: 'name', body: 'description', image: '', meta: 'price', meta_prefix: '$', layout: 'feature' },
    'icon-section': { resource: 'services', title: 'name', body: 'description', image: '', meta: 'price', meta_prefix: '$', layout: 'feature' },
  },
  health_fitness: {
    pricing: { resource: 'packages', title: 'name', body: 'features', image: '', meta: 'price', meta_prefix: '$', layout: 'card' },
    team: { resource: 'operators', title: 'name', body: 'specialty', image: '', meta: 'role', meta_prefix: '', layout: 'card' },
  },
  restaurant: {
    team: { resource: 'operators', title: 'name', body: 'role', image: '', meta: '', meta_prefix: '', layout: 'card' },
    booking: DEFAULT_WIDGET_BINDINGS.booking,
    form: DEFAULT_WIDGET_BINDINGS.booking,
  },
  realestate: {
    portfolio: DEFAULT_WIDGET_BINDINGS.listings,
  },
  personal: {
    blog: { resource: 'posts', title: 'title', body: 'content', image: '', meta: '', meta_prefix: '', layout: 'card' },
  },
  education: {
    blog: DEFAULT_WIDGET_BINDINGS.courses,
  },
  ecommerce: {
    pricing: {
      resource: 'products',
      title: 'name',
      body: 'description',
      image: 'image_url',
      meta: 'price',
      meta_prefix: '$',
      layout: 'card',
    },
    'features-grid': { resource: 'products', title: 'name', body: 'description', image: 'image_url', meta: 'price', meta_prefix: '$', layout: 'feature' },
    'icon-section': { resource: 'products', title: 'name', body: 'description', image: 'image_url', meta: 'price', meta_prefix: '$', layout: 'feature' },
  },
};

export function fallbackWidgetBindings(genreId = 'business') {
  const out = { ...DEFAULT_WIDGET_BINDINGS };
  const overrides = GENRE_WIDGET_OVERRIDES[genreId] || {};
  return { ...out, ...overrides };
}

export function gridAttrsFromBinding(binding) {
  if (!binding) return '';
  const parts = [
    `data-resource-list="${binding.resource}"`,
    `data-resource-title="${binding.title}"`,
    `data-resource-body="${binding.body}"`,
    `data-resource-layout="${binding.layout || 'card'}"`,
  ];
  if (binding.image) parts.push(`data-resource-image="${binding.image}"`);
  if (binding.meta) {
    parts.push(`data-resource-meta="${binding.meta}"`);
    if (binding.meta_prefix) parts.push(`data-resource-meta-prefix="${binding.meta_prefix}"`);
  }
  return parts.join(' ');
}

const GRID_SELECTOR_PATTERNS = [
  /(<div[^>]*class="[^"]*operate-resource-grid[^"]*"[^>]*)>/,
  /(<div[^>]*data-widget-content-grid[^>]*)>/,
];

export function applyWidgetBinding(html, widgetId, binding) {
  if (!html || !binding) return html;
  const attrs = gridAttrsFromBinding(binding);
  const marker = `data-operate-widget="${widgetId}"`;
  let next = html;
  if (!next.includes(marker)) {
    next = next.replace(
      /(<section[^>]*class="[^"]*widget-section[^"]*"[^>]*)>/,
      `$1 ${marker}>`,
    );
  }
  for (const pattern of GRID_SELECTOR_PATTERNS) {
    if (pattern.test(next)) {
      return next.replace(pattern, `$1 ${attrs}>`);
    }
  }
  return next;
}

export function operateSectionForWidget(widgetId, binding, innerHtml) {
  const attrs = gridAttrsFromBinding(binding);
  return `<div class="operate-resource-grid" ${attrs} style="display: grid; gap: 24px;">${innerHtml}</div>`;
}

export const OPERATE_EMPTY_GRID_MSG = `
  <div class="operate-resource-empty" style="grid-column: 1 / -1; text-align: center; padding: 40px 20px; background: #f8fafc; border-radius: 16px; color: #64748b;">
    Add items in Operate to populate this section.
  </div>`;
